import { useMemo } from "react";
import { useServerTable } from "../../../components/table/tableFeatures";
import { useDebounce } from "@reactuses/core";
import { useTranslation } from "react-i18next";
import { MdRotateLeft } from "react-icons/md";
import {
  useGetV4CategoryReportByCategory,
  type CategoryReportByCategoryRow,
} from "../../../modules/perf_reports/api/category/v4CategoryReportByCategory";
import { css } from "../../../../styled-system/css";
import { HStack, VStack } from "../../../../styled-system/jsx";
import { RT8ColumnSelector } from "../../../components/core/blocks/MetricSelector";
import { Button } from "../../../components/core/basic/Button";
import { SearchInput } from "../../../components/fields/base/TextField";
import { RT8Pagination } from "../../../components/table/Pagination";
import { Table } from "../../../components/table/Table";
import { createColumnBuilder } from "../../../components/table/columnBuilder";
import { useBlockWindowQuery } from "../../../components/table/useBlockWindowQuery";
import { idsToVisibilityState, resetTable } from "../../../components/table/utils";
import { usePersistedTableState } from "../../../components/table/usePersistedTableState";
import { RT8FilterWidget } from "../../../modules/merchandising/components/tables/FilterWidget";
import { useStandardEntityMetrics } from "../../../modules/merchandising/components/tables/metricUtils";
import { CategoryCell } from "../../../components/custom/merchandise/cells/CategoryCell";

const categoryColumnHelper = createColumnBuilder<CategoryReportByCategoryRow>();

export const ContentCategoriesTable = () => {
  const { t } = useTranslation(["product_quality", "common"]);
  const standardMetricIds = useStandardEntityMetrics("content");

  const columns = useMemo(
    () => [
      categoryColumnHelper.accessor("category_name", {
        id: "category_name",
        header: t(($) => $.product_quality["Category"]),
        minSize: 250,
        size: 400,
        maxSize: 500,
        cell: ({ row }) => (
          <CategoryCell original={row.original} buildLink={(id) => `/user/content/quality/category/${id}`} />
        ),
        meta: {
          columnSelectorOptions: {
            label: t(($) => $.product_quality["Category"]),
          },
        },
        enableColumnFilter: false,
      }),
      ...standardMetricIds.map((id) => categoryColumnHelper.metric(id)),
    ],
    [standardMetricIds, t],
  );

  const defaultHiddenCols = idsToVisibilityState(
    [
      "category_name",
      "sales",
      "units",
      "seo_score",
      "buyability_score",
      "ctr",
      "conversion_rate",
      "ad_spend",
      "ad_sales",
      "acos",
      "clicks",
      "impressions",
      "orders",
      "total_acos",
    ],
    columns.flatMap((column) => column.id ?? []),
  );

  const { state, setters, initialState } = usePersistedTableState({
    persistKey: "contentCategoriesTable",
    initialState: {
      columnVisibility: defaultHiddenCols,
      columnPinning: { start: ["category_name"], end: [] },
      sorting: [{ id: "seo_score", desc: true }],
      pagination: { pageIndex: 0, pageSize: 50 },
    },
  });

  const debouncedSearch = useDebounce(state.globalFilter, 500);

  const getCategoryOptions = useGetV4CategoryReportByCategory();
  const categoryQuery = useBlockWindowQuery(getCategoryOptions, {
    pagination: state.pagination,
    sorting: state.sorting,
    filters: state.columnFilters,
    search: debouncedSearch,
  });

  const table = useServerTable({
    data: categoryQuery.data?.data,
    columns,
    initialState,
    state,
    ...setters,
    alternateSortingEnabled: true,
    getRowId: (row) => row.category_id.toString(),
    rowCount: categoryQuery.data?.count || 0,
    meta: {
      showSkeleton: categoryQuery.isFetching && (!categoryQuery.data || categoryQuery.isPlaceholderData),
    },
  });

  return (
    <div>
      <HStack padding={"small"} fontSize={"large"}>
        <VStack flex="1" alignItems={"start"}>
          <HStack width={"100%"}>
            <Button
              variant={"contentOnly"}
              onPress={() => {
                resetTable(table);
              }}
              fontSize={"xxlarge"}
            >
              <MdRotateLeft />
            </Button>
            <RT8ColumnSelector table={table} />
            <SearchInput
              aria-label={t(($) => $.common.actions.Search)}
              isExpandable
              css={{ flex: "0 1 350px" }}
              value={table.state.globalFilter}
              onChange={table.setGlobalFilter}
            />
          </HStack>
          <RT8FilterWidget table={table} />
        </VStack>
        <RT8Pagination className={css({ marginLeft: "auto" })} table={table} />
      </HStack>
      <Table table={table} className={css({ minWidth: "100%" })} />
    </div>
  );
};
